import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Loader2, RotateCcw, Search, X } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CommercialGate from "../../components/commercial/CommercialGate";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import CountryPicker from "../../components/CountryPicker";
import MapPinPicker from "../../components/MapPinPicker";
import { api } from "../../lib/api";
import { setPageTitle } from "../../lib/format";
import { COUNTRIES } from "../../lib/countries";

const PROPERTY_TYPES = [
  ["surface_lot", "Surface lot"], ["garage", "Garage"], ["underground", "Underground"],
  ["residential", "Residential"], ["hotel", "Hotel"], ["condo", "Condo"], ["mixed", "Mixed use"], ["other", "Other"],
];

const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";

const newDraftId = () =>
  "drf_" + Array.from(crypto.getRandomValues(new Uint8Array(8))).map((b) => b.toString(16).padStart(2, "0")).join("");

const relTime = (d) => {
  if (!d) return "";
  const s = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 1000));
  if (s < 10) return "just now";
  if (s < 60) return `${s} seconds ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  return new Date(d).toLocaleString();
};

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1.5 text-[11px] text-zinc-600">{hint}</p>}
    </div>
  );
}

// CountryPicker is shared (components/CountryPicker) — complete ISO list,
// searchable, keyboard-accessible, identical across host workflows.

const ACCEPTED_IMG = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX_IMG_BYTES = 15 * 1024 * 1024;

function NewPropertyBody() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "", country: "CA", address: "", region: "", postal_code: "", property_type: "surface_lot", property_type_other: "",
    capacity_total: "", instructions: "",
  });
  const [pin, setPin] = useState(null); // {lat, lng} — map is the ONLY way to set coordinates
  const [timezone, setTimezone] = useState(null);
  // Country/location mismatch — blocks save until corrected (PRD §4): never
  // silently persist a country that disagrees with the pinned coordinates.
  const [geoMismatch, setGeoMismatch] = useState(null); // {detected: "US"}
  const countryName = (cc) => COUNTRIES.find(([c]) => c === cc)?.[1] || cc;

  // Changing the country invalidates the previous address/pin/coords — they
  // belonged to the old country's search space.
  const changeCountry = (c) => {
    if (c === form.country) return;
    setForm((f) => ({ ...f, country: c, address: "" }));
    setPin(null);
    setTimezone(null);
    setGeoMismatch(null);
  };
  const [images, setImages] = useState([]);
  const [uploads, setUploads] = useState([]); // {key, file, name, pct, status, error}
  const [busy, setBusy] = useState(false);

  // ---- draft / autosave state ----
  const [draftId, setDraftId] = useState(() => searchParams.get("draft") || null);
  const draftIdRef = useRef(draftId);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [lastSaved, setLastSaved] = useState(null);
  const [showLeave, setShowLeave] = useState(false);
  const hydrated = useRef(false);
  const allowNav = useRef(false);
  useEffect(() => setPageTitle("New Property"), []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dirty = !!(form.name.trim() || form.address.trim() || pin || images.length || form.capacity_total);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const completion = useMemo(() => {
    const checks = [
      form.name.trim().length >= 2,
      !!form.address.trim(),
      !!pin,
      images.length > 0,
      !!parseInt(form.capacity_total, 10),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [form, pin, images]);

  const missing = useMemo(() => {
    const m = [];
    if (form.name.trim().length < 2) m.push("property name");
    if (!form.address.trim()) m.push("address");
    if (!pin) m.push("location pin");
    if (!images.length) m.push("photos");
    if (!parseInt(form.capacity_total, 10)) m.push("capacity");
    return m;
  }, [form, pin, images]);

  // Restore a saved draft (Continue from My Properties)
  useEffect(() => {
    const d = searchParams.get("draft");
    if (!d) { const t = setTimeout(() => { hydrated.current = true; }, 400); return () => clearTimeout(t); }
    api.commercialPropertyDraft(d).then((r) => {
      const st = r.state || {};
      setForm((f) => ({ ...f, ...(st.form || {}) }));
      setPin(st.pin || null);
      setTimezone(st.timezone || null);
      setImages(Array.isArray(st.images) ? st.images : []);
      setLastSaved(r.updated_at || null);
      setSaveState(r.updated_at ? "saved" : "idle");
      setTimeout(() => { hydrated.current = true; }, 400);
    }).catch((e) => { toast.error(e.message); hydrated.current = true; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = async () => {
    if (!dirtyRef.current) return true;
    if (!draftIdRef.current) { draftIdRef.current = newDraftId(); setDraftId(draftIdRef.current); }
    setSaveState("saving");
    try {
      await api.commercialPropertyDraftSave(draftIdRef.current, {
        completion, state: { form, pin, timezone, images },
      });
      setSaveState("saved");
      setLastSaved(new Date());
      return true;
    } catch {
      setSaveState("error");
      return false;
    }
  };
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  // Intelligent autosave — debounced, only after meaningful change
  useEffect(() => {
    if (!hydrated.current || !dirty) return;
    const t = setTimeout(() => saveDraftRef.current(), 1500);
    return () => clearTimeout(t);
  }, [form, pin, timezone, images, completion, dirty]);

  // Back-button guard — the draft is server-side, so leaving is always safe,
  // but confirm so an accidental back never feels like data loss.
  useEffect(() => {
    const onPop = () => {
      if (!dirtyRef.current || allowNav.current) return;
      window.history.pushState(null, "", window.location.href);
      setShowLeave(true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const leaveNow = async () => {
    allowNav.current = true;
    await saveDraftRef.current();
    navigate("/commercial/properties");
  };

  const applyLocation = async (lat, lng, address) => {
    setPin({ lat, lng });
    if (address) setForm((f) => ({ ...f, address }));
    try {
      const r = await api.geoTimezone(lat, lng);
      setTimezone(r.timezone);
    } catch { setTimezone(null); }
    if (!address) {
      try {
        const r = await api.geoReverse(lat, lng);
        if (r.formatted_address) setForm((f) => ({ ...f, address: r.formatted_address }));
        // Country-component validation (PRD §4): a pin in another country must
        // be corrected, never silently saved as inconsistent data.
        if (r.country_code && r.country_code.toUpperCase() !== form.country) {
          setGeoMismatch({ detected: r.country_code.toUpperCase() });
        } else setGeoMismatch(null);
      } catch { /* reverse geocode is best-effort validation */ }
    } else setGeoMismatch(null);
  };

  const onAddressSelect = (s) => {
    // The autocomplete already refuses known country mismatches; double-guard
    // here so a fabricated result can never set coordinates across countries.
    if (s.country_code && String(s.country_code).toUpperCase() !== form.country) {
      setGeoMismatch({ detected: String(s.country_code).toUpperCase() });
      return;
    }
    setGeoMismatch(null);
    setForm((f) => ({ ...f, address: s.description || [s.main_text, s.secondary_text].filter(Boolean).join(", "),
                       region: s.region || f.region, postal_code: s.postal_code || f.postal_code }));
    if (s.lat != null && s.lng != null) applyLocation(s.lat, s.lng, null);
  };

  // ---- photo upload (desktop + mobile web: library / camera / files) ----
  const uploading = uploads.some((u) => u.status === "uploading");
  const batchTotal = uploads.length;
  const batchDone = uploads.filter((u) => u.status === "done").length;

  const startUpload = (entry) => {
    setUploads((us) => us.map((u) => (u.key === entry.key ? { ...u, status: "uploading", pct: 0, error: "" } : u)));
    api.uploadListingPhoto(entry.file, (pct) =>
      setUploads((us) => us.map((u) => (u.key === entry.key ? { ...u, pct } : u)))
    ).then((r) => {
      setImages((imgs) => [...imgs, r.url]);
      setUploads((us) => us.map((u) => (u.key === entry.key ? { ...u, status: "done", pct: 100 } : u)));
    }).catch((err) => {
      setUploads((us) => us.map((u) => (u.key === entry.key ? { ...u, status: "error", error: err.message } : u)));
    });
  };

  const uploadPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploads((us) => us.filter((u) => u.status !== "done")); // clear finished batch
    const room = 10 - images.length - uploads.filter((u) => u.status !== "done").length;
    files.slice(0, Math.max(0, room)).forEach((f) => {
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const entry = { key, file: f, name: f.name || "photo", pct: 0, status: "uploading", error: "" };
      if (!ACCEPTED_IMG.includes((f.type || "").toLowerCase()) && !/\.(heic|heif)$/i.test(f.name || "")) {
        entry.status = "error"; entry.error = "Unsupported image format — use JPG, PNG, WebP or HEIC";
      } else if (f.size > MAX_IMG_BYTES) {
        entry.status = "error"; entry.error = "Image is too large — max 15 MB per photo";
      }
      setUploads((us) => [...us, entry]);
      if (entry.status === "uploading") startUpload(entry);
    });
  };

  const moveImage = (idx, dir) => {
    setImages((imgs) => {
      const j = idx + dir;
      if (j < 0 || j >= imgs.length) return imgs;
      const next = [...imgs];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!pin) { toast.error("Confirm the exact property location on the map."); return; }
    if (geoMismatch) {
      toast.error(`This pin is in ${countryName(geoMismatch.detected)} — switch the country or move the pin before saving.`);
      return;
    }
    setBusy(true);
    try {
      const p = await api.commercialPropertyCreate({
        ...form,
        property_type_other: form.property_type === "other" ? form.property_type_other : undefined,
        lat: pin.lat, lng: pin.lng,
        capacity_total: parseInt(form.capacity_total, 10),
        instructions: form.instructions || undefined,
        images,
        // timezone intentionally omitted — the server derives the canonical IANA
        // timezone from the confirmed coordinates.
      });
      if (draftIdRef.current) {
        allowNav.current = true;
        api.commercialPropertyDraftDelete(draftIdRef.current).catch(() => {});
      }
      toast.success(`Property created — code ${p.property_code}`);
      navigate(`/commercial/properties/${p.property_id}`);
    } catch (err) {
      toast.error(err.message); // form state is untouched — nothing is lost on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <form id="property-new-form" onSubmit={submit} className="card-dark p-6 sm:p-8 space-y-5" data-testid="property-new-form">
        {/* Completion indicator */}
        <div className="rounded-2xl border border-[#27272A] bg-white/[0.02] p-4" data-testid="setup-completion">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Property setup</p>
            <p className="text-xs font-bold text-white tabular-nums" data-testid="setup-completion-pct">{completion}%</p>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-[#1F2328] overflow-hidden">
            <div className="h-full rounded-full bg-[#E50914] transition-[width] duration-500" style={{ width: `${completion}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-zinc-600" data-testid="setup-completion-missing">
            {missing.length
              ? <>Still needed: <span className="text-zinc-400">{missing.join(" · ")}</span></>
              : "Ready to create — categories, pricing, verification and payout complete on the setup page before publishing."}
          </p>
        </div>

        <Field label="Property name">
          <input required minLength={2} maxLength={120} value={form.name} onChange={set("name")}
                 placeholder="Downtown Tower Garage" className={inputCls} data-testid="property-name" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Country" hint="Chosen first — address suggestions are filtered to this country.">
            <CountryPicker value={form.country} onChange={changeCountry} testId="property-country" />
          </Field>
          <Field label="Property type">
            <select value={form.property_type} onChange={set("property_type")} className={inputCls} data-testid="property-type">
              {PROPERTY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        {form.property_type === "other" && (
          <Field label="Enter property type" hint="e.g. University, Shopping Mall, Private Lot, Event Venue">
            <input required minLength={2} maxLength={60} value={form.property_type_other} onChange={set("property_type_other")}
                   placeholder="Shopping Mall" className={inputCls} data-testid="property-type-other" />
          </Field>
        )}
        <Field label="Street address" hint={`Start typing and pick a suggestion — results are filtered to ${countryName(form.country)}.`}>
          <AddressAutocomplete value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                               onSelect={onAddressSelect} country={form.country}
                               placeholder="100 King St W, Toronto, ON" testId="property-address" />
        </Field>
        <Field label="Confirm exact property location"
               hint="Drag the pin (or tap the map) to the exact parking entrance — critical for condos, hotels, malls and office buildings.">
          <MapPinPicker lat={pin?.lat} lng={pin?.lng} onPick={(la, ln) => applyLocation(la, ln, null)} testId="property-map" />
          {geoMismatch && (
            <div className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 leading-relaxed"
                 data-testid="property-geo-mismatch" role="alert">
              This pin is in <span className="font-semibold">{countryName(geoMismatch.detected)}</span>, but the selected country is <span className="font-semibold">{countryName(form.country)}</span>.{" "}
              Move the pin, or{" "}
              <button type="button" className="underline underline-offset-2 font-semibold text-amber-200 hover:text-white transition-colors"
                      data-testid="property-geo-switch-country"
                      onClick={() => { setForm((f) => ({ ...f, country: geoMismatch.detected })); setGeoMismatch(null); }}>
                switch the country to {countryName(geoMismatch.detected)}
              </button>.
            </div>
          )}
          <p className="mt-2 text-xs text-zinc-500" data-testid="property-timezone-display">
            {pin ? <>Property timezone (auto-detected): <span className="text-white font-semibold">{timezone || "resolving…"}</span></>
                 : "Pin the property on the map — timezone is detected automatically."}
          </p>
        </Field>

        <Field label="Property photos"
               hint="JPG, PNG, WebP or HEIC · up to 15 MB each · at least one photo is required before the property can be published.">
          <div className="flex flex-wrap gap-2.5" data-testid="property-images">
            {images.map((u, idx) => (
              <div key={u} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#27272A]" data-testid={`property-image-${idx}`}>
                <img src={u} alt={`Property ${idx + 1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => setImages((imgs) => imgs.filter((x) => x !== u))} aria-label="Remove photo"
                        className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white" data-testid="property-image-remove">
                  <X className="w-3 h-3" />
                </button>
                <div className="absolute bottom-1 left-1 flex gap-1">
                  <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} aria-label="Move photo left"
                          className="p-1 rounded-full bg-black/70 text-white disabled:opacity-30" data-testid={`property-image-left-${idx}`}>
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === images.length - 1} aria-label="Move photo right"
                          className="p-1 rounded-full bg-black/70 text-white disabled:opacity-30" data-testid={`property-image-right-${idx}`}>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
            {uploads.map((u) => (
              <div key={u.key} className="w-20 h-20 rounded-lg border border-[#27272A] bg-[#0A0D10] flex flex-col items-center justify-center px-1.5 text-center"
                   data-testid={`property-upload-${u.status}`}>
                {u.status === "uploading" && (<><Loader2 className="w-4 h-4 animate-spin text-[#FF3B45]" /><span className="mt-1 text-[10px] text-zinc-400 tabular-nums">{u.pct}%</span></>)}
                {u.status === "done" && (<><Check className="w-4 h-4 text-emerald-400" /><span className="mt-1 text-[10px] text-emerald-400">Done</span></>)}
                {u.status === "error" && (
                  <>
                    <button type="button" onClick={() => startUpload(u)} className="flex flex-col items-center" data-testid="property-upload-retry">
                      <RotateCcw className="w-4 h-4 text-[#FF3B45]" /><span className="mt-1 text-[10px] text-[#FF3B45] font-semibold">Retry</span>
                    </button>
                    <button type="button" onClick={() => setUploads((us) => us.filter((x) => x.key !== u.key))} aria-label="Dismiss failed upload"
                            className="absolute top-1 right-1 text-zinc-500" data-testid="property-upload-dismiss"><X className="w-3 h-3" /></button>
                  </>
                )}
              </div>
            ))}
            {images.length + uploads.filter((u) => u.status !== "done").length < 10 && (
              <label className="w-20 h-20 rounded-lg border border-dashed border-[#27272A] flex flex-col items-center justify-center text-xs text-zinc-500 cursor-pointer hover:border-[#E50914]/50 transition-colors"
                     data-testid="property-image-upload">
                + Add Photos
                <input type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif,.heic,.heif" multiple className="hidden" onChange={uploadPhotos} />
              </label>
            )}
          </div>
          {uploading && (
            <p className="mt-2 text-[11px] text-zinc-400" data-testid="upload-progress">
              Uploading {Math.min(batchDone + 1, batchTotal)} of {batchTotal}…
            </p>
          )}
          {uploads.some((u) => u.status === "error") && (
            <p className="mt-2 text-[11px] text-[#FF6B6B]" data-testid="upload-error">
              {uploads.find((u) => u.status === "error")?.error} — tap Retry.
            </p>
          )}
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Total capacity (spots)">
            <input required type="number" min={1} max={10000} value={form.capacity_total} onChange={set("capacity_total")}
                   placeholder="100" className={inputCls} data-testid="property-capacity" />
          </Field>
          <Field label="Parking instructions (shown to drivers)">
            <input maxLength={500} value={form.instructions} onChange={set("instructions")}
                   placeholder="Enter via Bay St ramp. Visitor spots on level P1." className={inputCls} data-testid="property-instructions" />
          </Field>
        </div>
        <p className="text-xs text-zinc-500 leading-relaxed">
          A unique 4-digit property code and branded QR sign are generated automatically. The property goes live only after
          categories, pricing, photos and payout (bank) setup are complete and you publish it. By creating and publishing a
          property you agree to the <Link to="/commercial-terms" target="_blank" rel="noreferrer"
          className="text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors"
          data-testid="property-commercial-terms-link">Commercial Host Agreement</Link>, the{" "}
          <Link to="/terms" target="_blank" rel="noreferrer"
          className="text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors">Terms of Service</Link>{" "}
          and the <Link to="/privacy" target="_blank" rel="noreferrer"
          className="text-[#FF3B45] underline underline-offset-2 hover:text-white transition-colors">Privacy Policy</Link>.
        </p>
      </form>

      {/* Sticky final action area — Save for Later + primary create CTA */}
      <div className="sticky bottom-0 z-30 mt-6" data-testid="wizard-action-bar">
        <div className="rounded-2xl border border-[#27272A] bg-[#0D1114]/95 backdrop-blur-md shadow-2xl shadow-black/60 px-4 pt-3 flex items-center gap-3"
             style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <p className="text-[11px] text-zinc-500 min-w-0 flex-1 truncate" data-testid="draft-save-state" aria-live="polite">
            {saveState === "saving" && "Saving…"}
            {saveState === "saved" && <>Saved <span className="text-zinc-600">· {relTime(lastSaved)}</span></>}
            {saveState === "error" && <span className="text-[#FF6B6B]">Save failed — check your connection</span>}
            {saveState === "idle" && (dirty ? "Unsaved changes" : "Draft autosaves as you type")}
          </p>
          <button type="button" disabled={!dirty || saveState === "saving"}
                  onClick={async () => { if (await saveDraft()) { toast.success("Draft saved — continue anytime from My Properties"); allowNav.current = true; navigate("/commercial/properties"); } }}
                  className="btn-secondary !py-2.5 !px-4 text-xs shrink-0 disabled:opacity-50" data-testid="save-for-later">
            Save for Later
          </button>
          <button type="submit" form="property-new-form" disabled={busy || uploading}
                  className="btn-primary !py-2.5 !px-4 text-xs sm:text-sm shrink-0 disabled:opacity-50 shadow-lg shadow-[#E50914]/25"
                  data-testid="property-create-submit">
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>) : "Create property & continue setup"}
          </button>
        </div>
      </div>

      {showLeave && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="leave-guard">
          <div className="card-dark w-full max-w-sm p-6">
            <p className="font-display font-bold text-lg">Your progress has been saved</p>
            <p className="text-sm text-zinc-500 mt-2">This listing is saved as a draft — continue exactly where you left off from My Properties.</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setShowLeave(false)} className="btn-secondary flex-1 text-sm" data-testid="leave-continue">Continue editing</button>
              <button onClick={leaveNow} className="btn-primary flex-1 text-sm" data-testid="leave-confirm">Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PropertyNew() {
  return (
    <CommercialGate>
      <div className="min-h-screen flex flex-col" data-testid="property-new-page">
        <Navbar />
        <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16 overflow-x-clip">
          <button onClick={() => window.history.back()} className="text-xs text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <p className="section-label mt-4">Commercial</p>
          <h1 className="type-display mt-3">New property</h1>
          <div className="mt-8"><NewPropertyBody /></div>
        </main>
        <Footer />
      </div>
    </CommercialGate>
  );
}
